import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CheckCircle2, ScanLine } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pub = axios.create({ baseURL: API });

export default function PublicCapturePage() {
  const { slug } = useParams();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", company: "", role: "", email: "", phone: "", address: "", eventName: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    pub.get(`/public/capture/${slug}`)
      .then(({ data }) => setInfo(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!consent) return setError("Merci de cocher le consentement pour être recontacté.");
    if (!form.firstName.trim() && !form.lastName.trim()) return setError("Merci d'indiquer votre nom.");
    if (!form.email.trim()) return setError("Merci d'indiquer votre email.");
    setSubmitting(true);
    try {
      await pub.post(`/public/capture/${slug}`, { ...form, consent });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 text-center">
        <div data-testid="public-capture-notfound">
          <p className="font-heading text-2xl font-extrabold text-slate-800">Lien introuvable</p>
          <p className="text-slate-500 mt-2">Ce lien de contact n'est plus valide.</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md w-full text-center bg-white rounded-3xl border border-slate-200 shadow-sm p-8" data-testid="public-capture-success">
          <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-9 h-9 text-emerald-500" />
          </div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-800 mt-5">Merci ! 🎉</h1>
          <p className="text-slate-600 mt-2 leading-relaxed">
            Vos coordonnées ont bien été transmises{info?.repName ? ` à ${info.repName}` : ""}. Vous serez recontacté(e) très prochainement.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6">
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-2 justify-center mb-6">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
            <ScanLine className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading font-extrabold text-xl tracking-tight text-slate-800">Kallitag</span>
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-7">
          {!info ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-extrabold text-slate-800">Laissez-moi vos coordonnées</h1>
              <p className="text-slate-500 mt-1.5 text-sm leading-relaxed">
                {info.repName ? <><b className="text-slate-700">{info.repName}</b>{info.company ? ` — ${info.company}` : ""} </> : ""}
                vous recontactera après le salon. Remplissez ce court formulaire.
              </p>

              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="public-capture-form">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Prénom" testid="pc-firstName" value={form.firstName} onChange={set("firstName")} />
                  <Field label="Nom" testid="pc-lastName" value={form.lastName} onChange={set("lastName")} />
                </div>
                <Field label="Email" type="email" testid="pc-email" value={form.email} onChange={set("email")} required />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Entreprise" testid="pc-company" value={form.company} onChange={set("company")} />
                  <Field label="Fonction" testid="pc-role" value={form.role} onChange={set("role")} />
                </div>
                <Field label="Téléphone" testid="pc-phone" value={form.phone} onChange={set("phone")} />
                <Field label="Adresse" testid="pc-address" value={form.address} onChange={set("address")} />
                <div className="space-y-1.5">
                  <Label className="text-slate-600">Un message ? (optionnel)</Label>
                  <Textarea data-testid="pc-message" value={form.message} onChange={set("message")} placeholder="Ce qui vous intéresse…" rows={2} />
                </div>

                <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 cursor-pointer">
                  <input type="checkbox" data-testid="pc-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-blue-600" />
                  <span className="text-xs text-slate-600 leading-relaxed">
                    J'accepte d'être recontacté(e) et que mes coordonnées soient traitées dans le cadre de cette prise de contact commerciale (RGPD).
                  </span>
                </label>

                {error && <p className="text-sm text-rose-600" data-testid="pc-error">{error}</p>}

                <Button type="submit" disabled={submitting} data-testid="pc-submit" className="w-full rounded-xl h-12 text-base font-semibold">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Envoyer mes coordonnées"}
                </Button>
              </form>
            </>
          )}
        </div>
        <p className="text-center text-xs text-slate-400 mt-5">Propulsé par Kallitag Lead Capture</p>
      </div>
    </div>
  );
}

function Field({ label, testid, type = "text", value, onChange, required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-slate-600">{label}{required && <span className="text-rose-500"> *</span>}</Label>
      <Input type={type} data-testid={testid} value={value} onChange={onChange} className="h-11" />
    </div>
  );
}
